import { LitElement, html, nothing } from 'lit';
import { currentLang, t } from '../i18n';
import { defineZenComponent } from './register';
import type { SearchResultRenderModel } from './shared';
import type { GoalWithSubtasks } from '../types';
import type { WorkListAction } from './work-list';
import { formatGoalDeadlineLabel, formatGoalEventLabel, isGoalDeadlineProvisional, isGoalOverdue, provisionalDeadlineCopy } from '../helpers';

/** The title with the searched words marked, so the reader sees why it matched. */
function marked(text: string, query: string) {
  const at = query ? text.toLowerCase().indexOf(query) : -1;
  if (at < 0) return text;
  return html`${text.slice(0, at)}<mark class="search-hit">${text.slice(at, at + query.length)}</mark>${marked(text.slice(at + query.length), query)}`;
}

/**
 * Search results as one row per goal, with the tasks whose names matched.
 * #2857: a row opens the same detail as the list, not the old goal card.
 */
export class ZenSearchPanel extends LitElement {
  static properties = {
    model: { attribute: false },
  };

  declare model: SearchResultRenderModel;

  constructor() {
    super();
    this.model = { query: '', resultCount: 0, goals: [] };
  }

  createRenderRoot() {
    return this;
  }

  private emit(detail: WorkListAction) {
    this.dispatchEvent(new CustomEvent<WorkListAction>('work-list-action', { detail, bubbles: true, composed: true }));
  }

  private renderMeta(goal: GoalWithSubtasks, done: boolean) {
    const en = currentLang === 'en';
    const overdue = !done && isGoalOverdue(goal);
    const eventLabel = formatGoalEventLabel(goal);
    const provisional = isGoalDeadlineProvisional(goal) ? provisionalDeadlineCopy() : null;
    return html`<span class="search-result-meta">
      ${goal.deadline ? html`<span class=${`search-result-chip${overdue ? ' overdue' : ''}`} title=${provisional ? provisional.title : nothing}>
        <span class="mi mi-xs" aria-hidden="true">${overdue ? 'error' : 'event'}</span>${provisional ? html`<span class="deadline-provisional" aria-label=${provisional.aria}>${provisional.tag}</span>` : nothing}${formatGoalDeadlineLabel(goal)}
      </span>` : nothing}
      ${eventLabel ? html`<span class="search-result-chip" title=${en ? 'Event time' : '開催日時'}>
        <span class="mi mi-xs" aria-hidden="true">schedule</span>${eventLabel}
      </span>` : nothing}
    </span>`;
  }

  private renderGoal(goal: GoalWithSubtasks) {
    const en = currentLang === 'en';
    const query = this.model.query;
    const done = goal.status === 'done';
    const tasks = [...(goal.subtasks || []), ...(goal.decisions || [])]
      .filter((st) => st.status !== 'invalidated' && st.title.toLowerCase().includes(query));
    return html`<li class=${`search-result${done ? ' done' : ''}`}>
      <button type="button" class="search-result-goal" @click=${() => this.emit({ action: 'search-open', goalId: goal.id, subtaskId: null })}>
        <span class=${`mi mi-sm search-result-state${done ? ' done' : ''}`} role="img" aria-label=${done ? (en ? 'Done' : '完了') : (en ? 'In progress' : '進行中')}>${done ? 'check_circle' : 'flag'}</span>
        <span class="search-result-body">
          <span class="search-result-title">${marked(goal.title, query)}</span>
          ${this.renderMeta(goal, done)}
        </span>
        <span class="mi mi-sm search-result-open" aria-hidden="true">chevron_right</span>
      </button>
      ${tasks.length > 0 ? html`<ul class="search-result-tasks">${tasks.map((st) => html`<li>
        <button type="button" class="search-result-task" @click=${() => this.emit({ action: 'search-open', goalId: goal.id, subtaskId: st.id })}>
          <span class="mi mi-xs" aria-hidden="true">${st.status === 'done' ? 'check' : 'subdirectory_arrow_right'}</span><span>${marked(st.title, query)}</span>
        </button>
      </li>`)}</ul>` : nothing}
    </li>`;
  }

  /** #3805: customers whose domain matched; a pick shows the list narrowed to that customer. */
  private renderCustomers() {
    const customers = this.model.customers ?? [];
    if (customers.length === 0) return nothing;
    const en = currentLang === 'en';
    return html`<div class="search-customers">
      <div class="search-results-header">${en ? 'Customers (guessed from addresses)' : '顧客（アドレスからの推定）'}</div>
      <ul class="search-customer-list">${customers.map(({ domain, count }) => html`<li>
        <button type="button" class="work-customer" title=${en ? `Show the goals with ${domain}` : `${domain} のゴールを一覧で表示`}
          @click=${() => this.emit({ action: 'customer-filter', domain, fromSearch: true })}>
          <span class="mi mi-xs" aria-hidden="true">apartment</span>${marked(domain, this.model.query)}<span class="search-customer-count">${en ? `${count} goals` : `${count}件`}</span>
        </button>
      </li>`)}</ul>
    </div>`;
  }

  render() {
    if (!this.model.query) {
      return html`<div class="search-hint">${t('searchHint')}</div>`;
    }
    if (this.model.resultCount === 0 && (this.model.customers ?? []).length === 0) {
      return html`
        <div class="empty-state" id="searchEmpty">
          <div class="empty-state-icon"><span class="mi" style="font-size:32px;">search_off</span></div>
          <div id="searchEmptyMsg">
            ${t('searchEmptyMsg', { query: this.model.query })}
          </div>
        </div>
      `;
    }
    return html`
      ${this.renderCustomers()}
      <div class="search-results-header">
        ${t('searchResultsCount', { count: this.model.resultCount })}
      </div>
      <ul class="search-results">${[...this.model.goals].sort((a, b) => Number(a.status === 'done') - Number(b.status === 'done')).map((goal) => this.renderGoal(goal))}</ul>
    `;
  }
}

defineZenComponent('zen-search-panel', ZenSearchPanel);
